/**
 * The core view-model: turns the raw, newest-first `typeMessageRow[]` this
 * screen holds into the exact ordered render rows (dividers, system lines,
 * tombstones, sender-grouping, mention runs) the bubble and divider
 * components draw (ISSUE-280 plan §5.7/WI-6).
 */

import type {
  typeMessageGameCard,
  typeMessageLinkPreview,
  typeMessagePdf,
  typeMessagePoll,
  typeMessageRow,
} from "./mergeMessagePages";
import { parseMentionRuns, type typeMentionRun } from "./messageText";
import { formatDayDividerLabel, formatMessageTime } from "./relativeTime";

export type typeRenderRow =
  | { key: string; kind: "day-divider"; label: string }
  | { key: string; kind: "season-divider"; label: string }
  | { key: string; kind: "system-line"; text: string }
  | { key: string; kind: "deleted" }
  | {
      key: string;
      kind: "message";
      messageId: string;
      /** Which bubble content to draw. Distinct from `kind` above, which is
       *  this render row's own type discriminant against dividers and
       *  tombstones. */
      messageKind: typeMessageRow["kind"];
      senderId: string;
      senderDisplayName: string;
      senderAvatarUrl: string | null;
      isOwn: boolean;
      showSenderHeader: boolean;
      bodyRuns: typeMentionRun[];
      isEdited: boolean;
      replyToPreview: string | null;
      reactions: { emoji: string; count: number; reactedByMe: boolean }[];
      timeLabel: string | null;
      sentAtIso: string;
      mediaObjectIds: string[] | null;
      poll: typeMessagePoll | null;
      /**
       * `messagingApi`'s `/message/send` and `/message/list` carry exactly
       * one game card per message -- there is no batch-share endpoint. "Two
       * or three game cards shared together" (R8) is therefore built here,
       * client side, as a fold over consecutive `gameCard` messages from the
       * same sender: `gameCards` holds one entry per real message, in
       * arrival order, so a lone share is a one-entry array and three shares
       * in a row become one three-entry array. Reactions, pin and delete on
       * a folded row apply to `messageId` alone, which is the LAST (most
       * recent) message in the run -- there is no server concept of a
       * combined reaction set across several messages to draw instead.
       */
      gameCards: { messageId: string; gameCard: typeMessageGameCard }[] | null;
      /** Populated only for `messageKind: "pdf"` (ISSUE-750 R2). */
      pdf: typeMessagePdf | null;
      /** Populated only for `messageKind: "gif"` (R3). */
      gifObjectId: string | null;
      /** ISSUE-1360. The link card drawn under the bubble, or null. */
      linkPreview: typeMessageLinkPreview | null;
    };

function localDateKey(iso: string): string {
  const date = new Date(iso);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

export function resolveConversationRows(input: {
  messages: readonly typeMessageRow[];
  viewerUserId: string;
  nowMs: number;
}): typeRenderRow[] {
  const oldestFirst = [...input.messages].reverse();
  const rows: typeRenderRow[] = [];

  let previousDateKey: string | null = null;
  let previousSenderId: string | null = null;

  for (const [i, message] of oldestFirst.entries()) {
    const dateKey = localDateKey(message.sentAtIso);

    if (dateKey !== previousDateKey) {
      rows.push({
        key: `day-divider-${dateKey}`,
        kind: "day-divider",
        label: formatDayDividerLabel(message.sentAtIso, input.nowMs),
      });
      previousDateKey = dateKey;
      previousSenderId = null;
    }

    if (message.isDeleted) {
      rows.push({ key: `deleted-${message.messageId}`, kind: "deleted" });
      previousSenderId = null;
      continue;
    }

    if (message.kind === "systemAnnouncement") {
      if (message.announcementKind === "seasonStarted") {
        rows.push({
          key: `season-divider-${message.messageId}`,
          kind: "season-divider",
          label: message.body ?? "A new season started",
        });
      } else {
        rows.push({
          key: `system-line-${message.messageId}`,
          kind: "system-line",
          text: message.body ?? "",
        });
      }
      previousSenderId = null;
      continue;
    }

    const showSenderHeader = message.senderId !== previousSenderId;
    const bodyRuns = parseMentionRuns(message.body ?? "");

    const next = oldestFirst[i + 1];
    const isLastOfRun =
      next?.senderId !== message.senderId ||
      next.isDeleted ||
      next.kind === "systemAnnouncement" ||
      localDateKey(next.sentAtIso) !== dateKey;
    const timeLabel = isLastOfRun ? formatMessageTime(message.sentAtIso) : null;

    rows.push({
      key: `message-${message.messageId}`,
      kind: "message",
      messageId: message.messageId,
      messageKind: message.kind,
      senderId: message.senderId,
      senderDisplayName: message.senderDisplayName,
      senderAvatarUrl: message.senderAvatarUrl,
      isOwn: message.senderId === input.viewerUserId,
      showSenderHeader,
      bodyRuns,
      isEdited: message.isEdited,
      replyToPreview: message.replyToPreview,
      reactions: message.reactions,
      timeLabel,
      sentAtIso: message.sentAtIso,
      mediaObjectIds: message.mediaObjectIds,
      poll: message.poll,
      pdf: message.pdf,
      gifObjectId: message.gifObjectId,
      linkPreview: message.linkPreview,
      gameCards:
        message.kind === "gameCard" && message.gameCard !== null
          ? [{ messageId: message.messageId, gameCard: message.gameCard }]
          : null,
    });
    previousSenderId = message.senderId;
  }

  return foldConsecutiveGameCardRows(rows).reverse();
}

/**
 * Folds a run of consecutive `gameCard` message rows from the same sender
 * into one row (R8's stacked/carousel treatment), so the message list never
 * renders "two separate messages" for cards shared back to back. Runs over
 * `rows` in the same oldest-first order they were built in, immediately
 * before the caller's final reverse. A day-divider, a different sender, or
 * any other message kind in between breaks the run, exactly the way
 * `showSenderHeader`'s adjacency check already does above.
 */
function foldConsecutiveGameCardRows(
  rows: readonly typeRenderRow[],
): typeRenderRow[] {
  const folded: typeRenderRow[] = [];

  for (const row of rows) {
    const previous = folded[folded.length - 1];

    if (
      row.kind === "message" &&
      row.messageKind === "gameCard" &&
      row.gameCards !== null &&
      previous?.kind === "message" &&
      previous.messageKind === "gameCard" &&
      previous.gameCards !== null &&
      previous.senderId === row.senderId
    ) {
      // `showSenderHeader` keeps the FIRST message's value: the avatar and
      // sender name draw once, at the top of the stack. Everything else
      // (messageId, reactions, timeLabel, ...) takes the LATEST message's
      // value, per this function's own doc comment above.
      folded[folded.length - 1] = {
        ...row,
        showSenderHeader: previous.showSenderHeader,
        gameCards: [...previous.gameCards, ...row.gameCards],
      };
      continue;
    }

    folded.push(row);
  }

  return folded;
}
